import React, { useState, useEffect } from "react";
import {
    DefaultButton,
    Stack,
    Text,
    Label,
    TextField,
    Spinner,
    SpinnerSize,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Dropdown,
    IDropdownOption,
    Image,
    ImageFit,
} from "@fluentui/react";
import { useNavigate } from "react-router-dom";
import { apiWrapper } from "../../services/request";
import { AIGCPIPLINEAPI } from "../../services/apis/aigc";
import { acquireToken } from "../../lib/acquireToken";
import style from "./index.module.scss";

interface Intent {
    Presenter: string;
    Purpose: string;
    Title: string;
}

interface IntentRecognitionResponse {
    Intents: Intent[];
    Thinking: string;
}

interface OutlineGenerationRequest {
    user_input: string;
    intent: Intent;
    deep_thinking: boolean;
    language: string;
    model: string;
    image_source: string;
}

interface WorkflowPlan {
    workflowId: string;
    substyle: string;
    workflowTemplateName: string;
}

interface ImageResult {
    imageUrl: string;
    imageName: string;
    colors: Dictionary<string>;
    templateResult?: TemplateResult;
    isGeneratingTemplate?: boolean;
    templatePollingInterval?: NodeJS.Timeout;
}

interface TemplateResult {
    status: string;
    fileUrl?: string;
    error?: string;
}

interface PlanStepResultMultiImages {
    status: string;
    workflowId: string;
    substyle: string;
    images: ImageResult[];
}

interface Dictionary<TValue> {
    [key: string]: TValue;
}

export const Clippy: React.FC = () => {
    const history = useNavigate();
    const [topic, setTopic] = useState<string>("");
    const [isLoadingIntent, setIsLoadingIntent] = useState<boolean>(false);
    const [isLoadingOutline, setIsLoadingOutline] = useState<boolean>(false);
    const [intentResults, setIntentResults] = useState<IntentRecognitionResponse | null>(null);
    const [selectedIntent, setSelectedIntent] = useState<Intent | null>(null);
    const [outlineResult, setOutlineResult] = useState<any>(null);
    const [outlineResponse, setOutlineResponse] = useState<any>(null);
    const [errorMessage, setErrorMessage] = useState<string>("");

    // Template plan generation states
    const [isGeneratingPlan, setIsGeneratingPlan] = useState<boolean>(false);
    const [isPollingPlan, setIsPollingPlan] = useState<boolean>(false);
    const [isWorkflowRunning, setIsWorkflowRunning] = useState<boolean>(false);
    const [workflowId, setWorkflowId] = useState<string>("");
    const [workflowTemplateName, setWorkflowTemplateName] = useState<string>("");
    const [planResult, setPlanResult] = useState<PlanStepResultMultiImages | null>(null);
    const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

    // Button click tracking states
    const [hasClickedAnalyzeIntent, setHasClickedAnalyzeIntent] = useState<boolean>(false);
    const [hasClickedGenerateOutline, setHasClickedGenerateOutline] = useState<boolean>(false);
    const [hasClickedStartWorkflow, setHasClickedStartWorkflow] = useState<boolean>(false);
    const [hasClickedStartWorkflowFromResponse, setHasClickedStartWorkflowFromResponse] = useState<boolean>(false);
    const [hasClickedGeneratePlanFromOutline, setHasClickedGeneratePlanFromOutline] = useState<boolean>(false);
    const [hasClickedGeneratePlanFromResponse, setHasClickedGeneratePlanFromResponse] = useState<boolean>(false);
    const [hasClickedRetry, setHasClickedRetry] = useState<boolean>(false);

    const handleClose = () => {
        history("/workflow");
    };

    const handleIntentRecognition = async () => {
        if (!topic.trim()) {
            setErrorMessage("Please enter a topic for intent recognition.");
            return;
        }

        setIsLoadingIntent(true);
        setHasClickedAnalyzeIntent(true);
        setErrorMessage("");
        setIntentResults(null);
        setSelectedIntent(null);
        setOutlineResult(null);
        setOutlineResponse(null);
        setPlanResult(null);
        setWorkflowId("");
        setWorkflowTemplateName("");

        try {
            const response = await apiWrapper({
                config: {
                    url: AIGCPIPLINEAPI.demoIntentRecognition,
                    baseURL: process.env.REACT_APP_AIGC_BASE_URL,
                    headers: {
                        Authorization: `Bearer ${await acquireToken()}`,
                        "Content-Type": "application/json",
                    },
                    method: "POST",
                    data: {
                        user_input: topic.trim(),
                        language: "en-us",
                    },
                },
            });

            setIntentResults(response);
        } catch (error: any) {
            setErrorMessage(`Intent recognition failed: ${error.message || "Unknown error"}`);
        } finally {
            setIsLoadingIntent(false);
            setHasClickedAnalyzeIntent(false);
        }
    };

    const handleGenerateOutline = async () => {
        if (!selectedIntent) {
            setErrorMessage("Please select an intent first.");
            return;
        }

        setIsLoadingOutline(true);
        setHasClickedGenerateOutline(true);
        setErrorMessage("");

        try {
            const request: OutlineGenerationRequest = {
                user_input: topic.trim(),
                intent: selectedIntent,
                deep_thinking: false,
                language: "en-us",
                model: "GPT",
                image_source: "AI",
            };

            const response = await apiWrapper({
                config: {
                    url: AIGCPIPLINEAPI.demoGenerateOutline,
                    baseURL: process.env.REACT_APP_AIGC_BASE_URL,
                    headers: {
                        Authorization: `Bearer ${await acquireToken()}`,
                        "Content-Type": "application/json",
                    },
                    method: "POST",
                    data: request,
                },
            });

            setOutlineResult(response.outline);
            setOutlineResponse(response);
        } catch (error: any) {
            setErrorMessage(`Outline generation failed: ${error.message || "Unknown error"}`);
        } finally {
            setIsLoadingOutline(false);
            setHasClickedGenerateOutline(false);
        }
    };

    const startPollingPlan = (workflowTemplateName: string, workflowId: string) => {
        console.log("startPollingPlan called with:", { workflowTemplateName, workflowId });
        setIsPollingPlan(true);

        const poll = async () => {
            try {
                const url = AIGCPIPLINEAPI.liveDemoV2PlanQuery.replace("{workflowTemplateName}", workflowTemplateName).replace("{workflowId}", workflowId);
                console.log("Polling URL:", url);

                const response = await apiWrapper({
                    config: {
                        url,
                        baseURL: process.env.REACT_APP_AIGC_BASE_URL,
                        headers: {
                            Authorization: `Bearer ${await acquireToken()}`,
                        },
                        method: "GET",
                    },
                });

                const result: PlanStepResultMultiImages = response;

                if (result.status === "COMPLETED") {
                    setPlanResult(result);
                    setIsPollingPlan(false);
                    setIsWorkflowRunning(false);
                    // Reset all click states when operation completes
                    setHasClickedStartWorkflow(false);
                    setHasClickedStartWorkflowFromResponse(false);
                    setHasClickedGeneratePlanFromOutline(false);
                    setHasClickedGeneratePlanFromResponse(false);
                    if (pollingInterval) {
                        clearInterval(pollingInterval);
                        setPollingInterval(null);
                    }
                } else if (result.status === "FAILED") {
                    setErrorMessage("Workflow execution failed. Please try again.");
                    setIsPollingPlan(false);
                    setIsWorkflowRunning(false);
                    // Reset all click states when operation fails
                    setHasClickedStartWorkflow(false);
                    setHasClickedStartWorkflowFromResponse(false);
                    setHasClickedGeneratePlanFromOutline(false);
                    setHasClickedGeneratePlanFromResponse(false);
                    if (pollingInterval) {
                        clearInterval(pollingInterval);
                        setPollingInterval(null);
                    }
                }
                // If status is still running, continue polling
            } catch (error: any) {
                setErrorMessage(`Polling failed: ${error.message || "Unknown error"}`);
                setIsPollingPlan(false);
                setIsWorkflowRunning(false);
                // Reset all click states when polling fails
                setHasClickedStartWorkflow(false);
                setHasClickedStartWorkflowFromResponse(false);
                setHasClickedGeneratePlanFromOutline(false);
                setHasClickedGeneratePlanFromResponse(false);
                if (pollingInterval) {
                    clearInterval(pollingInterval);
                    setPollingInterval(null);
                }
            }
        };

        // Poll every 5 seconds
        const interval = setInterval(poll, 5000);
        setPollingInterval(interval);

        // Initial poll
        poll();
    };

    const handleStartWorkflow = async () => {
        if (!outlineResult) {
            setErrorMessage("Please generate an outline first.");
            return;
        }

        setIsWorkflowRunning(true);
        setHasClickedStartWorkflow(true);
        setErrorMessage("");
        setPlanResult(null);

        try {
            const response = await apiWrapper({
                config: {
                    url: AIGCPIPLINEAPI.liveDemoV2PlanRun,
                    baseURL: process.env.REACT_APP_AIGC_BASE_URL,
                    headers: {
                        Authorization: `Bearer ${await acquireToken()}`,
                        "Content-Type": "application/json",
                    },
                    method: "POST",
                    data: outlineResult.presentation,
                    params: {
                        topic: topic.trim(),
                        style: "Modern",
                        substyle: "Geometry_Geometry_Cropping_Geometry_Corporate_FullVisual",
                        imageModel: "DALLE",
                        languageModel: "GPT",
                        imageSource: "AI",
                        debug: true,
                        version: "2",
                    },
                },
            });

            console.log("Raw API Response:", response);
            console.log("Response type:", typeof response);
            console.log("Response keys:", Object.keys(response));

            const workflowPlan: WorkflowPlan = response;
            console.log("Workflow Plan Response:", workflowPlan);
            console.log("WorkflowId:", workflowPlan.workflowId);
            console.log("WorkflowTemplateName:", workflowPlan.workflowTemplateName);

            setWorkflowId(workflowPlan.workflowId);
            setWorkflowTemplateName(workflowPlan.workflowTemplateName);
            startPollingPlan(workflowPlan.workflowTemplateName, workflowPlan.workflowId);
        } catch (error: any) {
            setErrorMessage(`Workflow start failed: ${error.message || "Unknown error"}`);
            setIsWorkflowRunning(false);
            setHasClickedStartWorkflow(false);
        }
    };

    const handleStartWorkflowFromResponse = async () => {
        if (!outlineResponse) {
            setErrorMessage("Please generate an outline first.");
            return;
        }

        setIsWorkflowRunning(true);
        setHasClickedStartWorkflowFromResponse(true);
        setErrorMessage("");
        setPlanResult(null);

        try {
            const response = await apiWrapper({
                config: {
                    url: AIGCPIPLINEAPI.liveDemoV2PlanRun,
                    baseURL: process.env.REACT_APP_AIGC_BASE_URL,
                    headers: {
                        Authorization: `Bearer ${await acquireToken()}`,
                        "Content-Type": "application/json",
                    },
                    method: "POST",
                    data: outlineResponse.presentation,
                    params: {
                        topic: topic.trim(),
                        style: "Modern",
                        substyle: "Geometry_Geometry_Cropping_Geometry_Corporate_FullVisual",
                        imageModel: "DALLE",
                        languageModel: "GPT",
                        imageSource: "AI",
                        debug: true,
                        version: "2",
                    },
                },
            });

            console.log("Raw API Response (from response):", response);
            console.log("Response type:", typeof response);
            console.log("Response keys:", Object.keys(response));

            const workflowPlan: WorkflowPlan = response;
            console.log("Workflow Plan Response:", workflowPlan);
            console.log("WorkflowId:", workflowPlan.workflowId);
            console.log("WorkflowTemplateName:", workflowPlan.workflowTemplateName);

            setWorkflowId(workflowPlan.workflowId);
            setWorkflowTemplateName(workflowPlan.workflowTemplateName);
            startPollingPlan(workflowPlan.workflowTemplateName, workflowPlan.workflowId);
        } catch (error: any) {
            setErrorMessage(`Workflow start failed: ${error.message || "Unknown error"}`);
            setIsWorkflowRunning(false);
            setHasClickedStartWorkflowFromResponse(false);
        }
    };

    const handleIntentSelection = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option && intentResults) {
            const selectedIndex = option.key as number;
            setSelectedIntent(intentResults.Intents[selectedIndex]);
        }
    };

    const getIntentDropdownOptions = (): IDropdownOption[] => {
        if (!intentResults) return [];

        return intentResults.Intents.map((intent, index) => ({
            key: index,
            text: intent.Title,
            data: intent,
        }));
    };

    const getSelectedIntentText = (): string => {
        if (!selectedIntent) return "";

        return JSON.stringify({
            Presenter: selectedIntent.Presenter,
            Purpose: selectedIntent.Purpose,
            Title: selectedIntent.Title,
        }, null, 2);
    };

    const handleGeneratePlanFromOutline = async () => {
        if (!outlineResult) {
            setErrorMessage("Please generate an outline first.");
            return;
        }

        setIsGeneratingPlan(true);
        setHasClickedGeneratePlanFromOutline(true);
        setErrorMessage("");
        setPlanResult(null);

        try {
            const response = await apiWrapper({
                config: {
                    url: AIGCPIPLINEAPI.liveDemoV2PlanRun,
                    baseURL: process.env.REACT_APP_AIGC_BASE_URL,
                    headers: {
                        Authorization: `Bearer ${await acquireToken()}`,
                        "Content-Type": "application/json",
                    },
                    method: "POST",
                    data: outlineResult.presentation,
                    params: {
                        topic: topic.trim(),
                        style: "Modern",
                        substyle: "Geometry_Geometry_Cropping_Geometry_Corporate_FullVisual",
                        imageModel: "DALLE",
                        languageModel: "GPT",
                        imageSource: "AI",
                        debug: true,
                        version: "2",
                    },
                },
            });

            const workflowPlan: WorkflowPlan = response;
            setWorkflowId(workflowPlan.workflowId);
            setWorkflowTemplateName(workflowPlan.workflowTemplateName);
            startPollingPlan(workflowPlan.workflowTemplateName, workflowPlan.workflowId);
        } catch (error: any) {
            setErrorMessage(`Plan generation failed: ${error.message || "Unknown error"}`);
        } finally {
            setIsGeneratingPlan(false);
            setHasClickedGeneratePlanFromOutline(false);
        }
    };

    const handleGeneratePlanFromResponse = async () => {
        if (!outlineResponse) {
            setErrorMessage("Please generate an outline first.");
            return;
        }

        setIsGeneratingPlan(true);
        setHasClickedGeneratePlanFromResponse(true);
        setErrorMessage("");
        setPlanResult(null);

        try {
            const response = await apiWrapper({
                config: {
                    url: AIGCPIPLINEAPI.liveDemoV2PlanRun,
                    baseURL: process.env.REACT_APP_AIGC_BASE_URL,
                    headers: {
                        Authorization: `Bearer ${await acquireToken()}`,
                        "Content-Type": "application/json",
                    },
                    method: "POST",
                    data: outlineResponse.presentation,
                    params: {
                        topic: topic.trim(),
                        style: "Modern",
                        substyle: "Geometry_Geometry_Cropping_Geometry_Corporate_FullVisual",
                        imageModel: "DALLE",
                        languageModel: "GPT",
                        imageSource: "AI",
                        debug: true,
                        version: "2",
                    },
                },
            });

            const workflowPlan: WorkflowPlan = response;
            setWorkflowId(workflowPlan.workflowId);
            setWorkflowTemplateName(workflowPlan.workflowTemplateName);
            startPollingPlan(workflowPlan.workflowTemplateName, workflowPlan.workflowId);
        } catch (error: any) {
            setErrorMessage(`Plan generation failed: ${error.message || "Unknown error"}`);
        } finally {
            setIsGeneratingPlan(false);
            setHasClickedGeneratePlanFromResponse(false);
        }
    };

    const handleRetryPlan = () => {
        console.log("handleRetryPlan - stored workflowId:", workflowId);
        console.log("handleRetryPlan - stored workflowTemplateName:", workflowTemplateName);

        setHasClickedRetry(true);

        if (workflowId && workflowTemplateName) {
            console.log("Retrying with stored values:", { workflowTemplateName, workflowId });
            startPollingPlan(workflowTemplateName, workflowId);
        } else {
            console.log("No stored workflow info, starting new workflow");
            handleStartWorkflow();
        }

        // Reset the retry click state after a short delay to allow the operation to start
        setTimeout(() => {
            setHasClickedRetry(false);
        }, 1000);
    };

    const startPollingTemplate = (imageName: string) => {
        console.log("startPollingTemplate called for image:", imageName);

        const poll = async () => {
            try {
                const url = AIGCPIPLINEAPI.themeAndTemplateQuery
                    .replace("{workflowTemplateName}", workflowTemplateName)
                    .replace("{workflowId}", workflowId);
                console.log("Polling Template URL:", url);

                const response = await apiWrapper({
                    config: {
                        url,
                        baseURL: process.env.REACT_APP_AIGC_BASE_URL,
                        headers: {
                            Authorization: `Bearer ${await acquireToken()}`,
                        },
                        method: "GET",
                        params: {
                            topic: topic.trim(),
                        },
                    },
                });

                console.log("Template Query Response:", response);

                if (response.status === "COMPLETED") {
                    // Update the specific image with the template result
                    setPlanResult((prevResult) => {
                        if (!prevResult) return prevResult;

                        const updatedImages = prevResult.images.map((img) => {
                            if (img.imageName === imageName) {
                                return {
                                    ...img,
                                    isGeneratingTemplate: false,
                                    templateResult: {
                                        status: response.status,
                                        fileUrl: response.fileUrl || response.result?.fileUrl,
                                    },
                                };
                            }
                            return img;
                        });

                        return { ...prevResult, images: updatedImages };
                    });

                    // Clear the polling interval for this image
                    setPlanResult((prevResult) => {
                        if (!prevResult) return prevResult;

                        const updatedImages = prevResult.images.map((img) => {
                            if (img.imageName === imageName && img.templatePollingInterval) {
                                clearInterval(img.templatePollingInterval);
                                return { ...img, templatePollingInterval: undefined };
                            }
                            return img;
                        });

                        return { ...prevResult, images: updatedImages };
                    });
                } else if (response.status === "FAILED") {
                    setErrorMessage(`Template generation failed for ${imageName}`);

                    // Update the specific image with error state
                    setPlanResult((prevResult) => {
                        if (!prevResult) return prevResult;

                        const updatedImages = prevResult.images.map((img) => {
                            if (img.imageName === imageName) {
                                return {
                                    ...img,
                                    isGeneratingTemplate: false,
                                    templateResult: {
                                        status: response.status,
                                        error: response.error || "Template generation failed",
                                    },
                                };
                            }
                            return img;
                        });

                        return { ...prevResult, images: updatedImages };
                    });

                    // Clear the polling interval for this image
                    setPlanResult((prevResult) => {
                        if (!prevResult) return prevResult;

                        const updatedImages = prevResult.images.map((img) => {
                            if (img.imageName === imageName && img.templatePollingInterval) {
                                clearInterval(img.templatePollingInterval);
                                return { ...img, templatePollingInterval: undefined };
                            }
                            return img;
                        });

                        return { ...prevResult, images: updatedImages };
                    });
                }
                // If status is still running, continue polling
            } catch (error: any) {
                setErrorMessage(`Template polling failed for ${imageName}: ${error.message || "Unknown error"}`);

                // Reset the image's state on error
                setPlanResult((prevResult) => {
                    if (!prevResult) return prevResult;

                    const updatedImages = prevResult.images.map((img) => {
                        if (img.imageName === imageName) {
                            return { ...img, isGeneratingTemplate: false };
                        }
                        return img;
                    });

                    return { ...prevResult, images: updatedImages };
                });

                // Clear the polling interval for this image
                setPlanResult((prevResult) => {
                    if (!prevResult) return prevResult;

                    const updatedImages = prevResult.images.map((img) => {
                        if (img.imageName === imageName && img.templatePollingInterval) {
                            clearInterval(img.templatePollingInterval);
                            return { ...img, templatePollingInterval: undefined };
                        }
                        return img;
                    });

                    return { ...prevResult, images: updatedImages };
                });
            }
        };

        // Poll every 3 seconds for template generation
        const interval = setInterval(poll, 3000);

        // Store the interval in the image's state
        setPlanResult((prevResult) => {
            if (!prevResult) return prevResult;

            const updatedImages = prevResult.images.map((img) => {
                if (img.imageName === imageName) {
                    return { ...img, templatePollingInterval: interval };
                }
                return img;
            });

            return { ...prevResult, images: updatedImages };
        });

        // Initial poll
        poll();
    };

    const handleGenerateTemplate = async (imageName: string) => {
        if (!workflowId || !workflowTemplateName) {
            setErrorMessage("No workflow information available. Please run a workflow first.");
            return;
        }

        setPlanResult((prevResult) => {
            if (!prevResult) return prevResult;
            const updatedImages = prevResult.images.map((img) => (
                img.imageName === imageName
                    ? { ...img, isGeneratingTemplate: true, templateResult: undefined }
                    : img
            ));
            return { ...prevResult, images: updatedImages };
        });

        try {
            const url = AIGCPIPLINEAPI.themeAndTemplateRun
                .replace("{workflowTemplateName}", workflowTemplateName)
                .replace("{workflowId}", workflowId);

            await apiWrapper({
                config: {
                    url,
                    baseURL: process.env.REACT_APP_AIGC_BASE_URL,
                    headers: {
                        Authorization: `Bearer ${await acquireToken()}`,
                        "Content-Type": "application/json",
                    },
                    method: "POST",
                    data: {
                        Topic: topic.trim(),
                        ImageName: imageName,
                    },
                    params: {
                        version: "2",
                    },
                },
            });

            startPollingTemplate(imageName);
        } catch (error: any) {
            setErrorMessage(`Template generation failed: ${error.message || "Unknown error"}`);

            setPlanResult((prevResult) => {
                if (!prevResult) return prevResult;
                const updatedImages = prevResult.images.map((img) => (
                    img.imageName === imageName
                        ? { ...img, isGeneratingTemplate: false }
                        : img
                ));
                return { ...prevResult, images: updatedImages };
            });
        }
    };

    // Cleanup polling interval on unmount
    useEffect(() => () => {
        if (pollingInterval) clearInterval(pollingInterval);
        if (planResult) {
            planResult.images.forEach((img) => {
                if (img.templatePollingInterval) {
                    clearInterval(img.templatePollingInterval);
                }
            });
        }
    }, [pollingInterval, planResult]);

    // Monitor workflow state changes
    useEffect(() => {
        console.log("workflowId state changed to:", workflowId);
    }, [workflowId]);

    useEffect(() => {
        console.log("workflowTemplateName state changed to:", workflowTemplateName);
    }, [workflowTemplateName]);

    return (
        <div className={style.clippy}>
            <div className={style.title}>
                <Text variant="xLarge" className={style.titleText}>Clippy Assistant</Text>
                <DefaultButton
                    text="Close"
                    iconProps={{ iconName: "Cancel" }}
                    onClick={handleClose}
                />
            </div>

            <div className={style.content}>
                <Stack tokens={{ childrenGap: 20 }}>
                    {errorMessage && (
                        <MessageBar
                            messageBarType={MessageBarType.error}
                            onDismiss={() => setErrorMessage("")}
                        >
                            {errorMessage}
                        </MessageBar>
                    )}

                    <div className={style.section}>
                        <div className={style.sectionHeader}>Intent Recognition</div>
                        <Stack tokens={{ childrenGap: 16, padding: "0 12px" }}>
                            <div className={style.inputSection}>
                                <Label>Topic:</Label>
                                <TextField
                                    value={topic}
                                    onChange={(_, newValue) => setTopic(newValue || "")}
                                    placeholder="Enter a topic for intent recognition (e.g., 'invoice')"
                                    multiline
                                    rows={3}
                                    maxLength={500}
                                />
                                <PrimaryButton
                                    text="Analyze Intent"
                                    onClick={handleIntentRecognition}
                                    disabled={isLoadingIntent || !topic.trim() || hasClickedAnalyzeIntent}
                                    iconProps={{ iconName: "Search" }}
                                />
                            </div>

                            {isLoadingIntent && (
                                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                    <Spinner size={SpinnerSize.small} />
                                    <Text>Analyzing intent...</Text>
                                </Stack>
                            )}

                            {intentResults && (
                                <div className={style.resultsSection}>
                                    <Text variant="mediumPlus" className={style.subsectionHeader}>
                                        Analysis Results
                                    </Text>

                                    <div className={style.thinkingSection}>
                                        <Label>Analysis Thinking:</Label>
                                        <TextField
                                            multiline
                                            rows={3}
                                            value={intentResults.Thinking}
                                            readOnly
                                            className={style.readOnlyTextField}
                                        />
                                    </div>

                                    <div className={style.intentSelectionSection}>
                                        <Label>
                                            Select Intent (
                                            {intentResults.Intents.length}
                                            {" "}
                                            available):
                                        </Label>
                                        <Dropdown
                                            placeholder="Choose an intent..."
                                            options={getIntentDropdownOptions()}
                                            onChange={handleIntentSelection}
                                            className={style.intentDropdown}
                                        />
                                    </div>

                                    {selectedIntent && (
                                        <div className={style.selectedIntentSection}>
                                            <Label>Selected Intent Details:</Label>
                                            <TextField
                                                multiline
                                                rows={8}
                                                value={getSelectedIntentText()}
                                                readOnly
                                                className={style.readOnlyTextField}
                                            />
                                        </div>
                                    )}

                                    <div className={style.generateButtonSection}>
                                        <PrimaryButton
                                            text="Generate Outline"
                                            onClick={handleGenerateOutline}
                                            disabled={!selectedIntent || isLoadingOutline || hasClickedGenerateOutline}
                                            iconProps={{ iconName: "Generate" }}
                                        />
                                        {isLoadingOutline && (
                                            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                                <Spinner size={SpinnerSize.small} />
                                                <Text>Generating outline...</Text>
                                            </Stack>
                                        )}
                                    </div>
                                </div>
                            )}
                        </Stack>
                    </div>

                    {outlineResult && (
                        <div className={style.section}>
                            <div className={style.sectionHeader}>Generated Outline</div>
                            <Stack tokens={{ childrenGap: 16, padding: "0 12px" }}>
                                <div className={style.outlineDisplaySection}>
                                    <Label>Outline Content:</Label>
                                    <div className={style.outlineTextbox}>
                                        <TextField
                                            multiline
                                            rows={12}
                                            value={JSON.stringify(outlineResult, null, 2)}
                                            readOnly
                                            className={style.readOnlyTextField}
                                        />
                                    </div>
                                    <div className={style.generateButtonSection}>
                                        <PrimaryButton
                                            text="Start Workflow"
                                            onClick={handleStartWorkflow}
                                            disabled={isWorkflowRunning || isGeneratingPlan || isPollingPlan || hasClickedStartWorkflow}
                                            iconProps={{ iconName: "Play" }}
                                        />
                                        {(isWorkflowRunning || isPollingPlan) && (
                                            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                                <Spinner size={SpinnerSize.small} />
                                                <Text>
                                                    {isWorkflowRunning && !isPollingPlan ? "Starting workflow..." : "Executing workflow..."}
                                                </Text>
                                            </Stack>
                                        )}
                                    </div>
                                </div>

                                <div className={style.planGenerationSection}>
                                    <Stack horizontal tokens={{ childrenGap: 12 }}>
                                        <PrimaryButton
                                            text="Generate Plan from Outline"
                                            onClick={handleGeneratePlanFromOutline}
                                            disabled={isWorkflowRunning || isGeneratingPlan || isPollingPlan || hasClickedGeneratePlanFromOutline}
                                            iconProps={{ iconName: "Design" }}
                                        />
                                        <PrimaryButton
                                            text="Generate Plan from Response"
                                            onClick={handleGeneratePlanFromResponse}
                                            disabled={isWorkflowRunning || isGeneratingPlan || isPollingPlan || hasClickedGeneratePlanFromResponse}
                                            iconProps={{ iconName: "Design" }}
                                        />
                                    </Stack>
                                    {(isGeneratingPlan || isPollingPlan) && (
                                        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                            <Spinner size={SpinnerSize.small} />
                                            <Text>
                                                {isGeneratingPlan ? "Generating plan..." : "Polling for results..."}
                                            </Text>
                                        </Stack>
                                    )}
                                </div>
                            </Stack>
                        </div>
                    )}

                    {outlineResponse && (
                        <div className={style.section}>
                            <div className={style.sectionHeader}>Outline Generation Response</div>
                            <Stack tokens={{ childrenGap: 16, padding: "0 12px" }}>
                                <div className={style.outlineResponseSection}>
                                    <Label>Full Response:</Label>
                                    <div className={style.outlineTextbox}>
                                        <TextField
                                            multiline
                                            rows={8}
                                            value={JSON.stringify(outlineResponse, null, 2)}
                                            readOnly
                                            className={style.readOnlyTextField}
                                        />
                                    </div>
                                    <div className={style.generateButtonSection}>
                                        <PrimaryButton
                                            text="Start Workflow from Response"
                                            onClick={handleStartWorkflowFromResponse}
                                            disabled={isWorkflowRunning || isGeneratingPlan || isPollingPlan || hasClickedStartWorkflowFromResponse}
                                            iconProps={{ iconName: "Play" }}
                                        />
                                        {(isWorkflowRunning || isPollingPlan) && (
                                            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                                <Spinner size={SpinnerSize.small} />
                                                <Text>
                                                    {isWorkflowRunning && !isPollingPlan ? "Starting workflow..." : "Executing workflow..."}
                                                </Text>
                                            </Stack>
                                        )}
                                    </div>
                                </div>
                            </Stack>
                        </div>
                    )}

                    {planResult && (
                        <div className={style.section}>
                            <div className={style.sectionHeader}>Template Plan Results</div>
                            <Stack tokens={{ childrenGap: 16, padding: "0 12px" }}>
                                <div className={style.planResultsSection}>
                                    <Text variant="mediumPlus" className={style.subsectionHeader}>
                                        Generated Images (
                                        {planResult.images.length}
                                        )
                                    </Text>
                                    <div className={style.imageGrid}>
                                        {planResult.images.map((image) => (
                                            <div key={image.imageName} className={style.imageCard}>
                                                <Image
                                                    src={image.imageUrl}
                                                    alt={image.imageName}
                                                    width={200}
                                                    height={150}
                                                    imageFit={ImageFit.cover}
                                                />
                                                <Text variant="small" className={style.imageName}>
                                                    {image.imageName}
                                                </Text>
                                                <div className={style.colorPalette}>
                                                    {Object.entries(image.colors).map(([colorName, colorValue]) => (
                                                        <div key={colorName} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                                            <div
                                                                className={style.colorSwatch}
                                                                style={{ backgroundColor: colorValue }}
                                                                title={`${colorName}: ${colorValue}`}
                                                            />
                                                            <div className={style.colorLabel}>{colorName}</div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Template Generation Section */}
                                                <div className={style.templateSection}>
                                                    <PrimaryButton
                                                        text="Generate Template"
                                                        onClick={() => handleGenerateTemplate(image.imageName)}
                                                        disabled={image.isGeneratingTemplate || !workflowId || !workflowTemplateName}
                                                        iconProps={{ iconName: "PowerPointLogo" }}
                                                    />

                                                    {image.isGeneratingTemplate && (
                                                        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                                            <Spinner size={SpinnerSize.small} />
                                                            <Text variant="small">Generating template...</Text>
                                                        </Stack>
                                                    )}

                                                    {image.templateResult && image.templateResult.status === "COMPLETED" && image.templateResult.fileUrl && (
                                                        <div className={style.templateResult}>
                                                            <Text variant="small" className={style.templateSuccess}>
                                                                Template generated successfully!
                                                            </Text>
                                                            <iframe
                                                                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(image.templateResult.fileUrl)}`}
                                                                width="100%"
                                                                height="400"
                                                                frameBorder="0"
                                                                title={`Template for ${image.imageName}`}
                                                                style={{
                                                                    borderRadius: 8,
                                                                    border: "1px solid #ccc",
                                                                    marginTop: "8px",
                                                                    maxWidth: "100%",
                                                                }}
                                                            />
                                                            <div style={{ marginTop: "8px" }}>
                                                                <a
                                                                    href={image.templateResult.fileUrl}
                                                                    download={`template-${image.imageName}.pptx`}
                                                                    style={{
                                                                        display: "inline-block",
                                                                        padding: "6px 16px",
                                                                        background: "#0078d4",
                                                                        color: "white",
                                                                        borderRadius: 4,
                                                                        textDecoration: "none",
                                                                        fontWeight: 500,
                                                                        fontSize: 14,
                                                                    }}
                                                                >
                                                                    Download PPTX
                                                                </a>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {image.templateResult && image.templateResult.status === "FAILED" && (
                                                        <div className={style.templateError}>
                                                            <Text variant="small" style={{ color: "red" }}>
                                                                Template generation failed:
                                                                {" "}
                                                                {image.templateResult.error}
                                                            </Text>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Stack>
                        </div>
                    )}

                    {errorMessage && planResult === null && (isWorkflowRunning || isGeneratingPlan || isPollingPlan) && (
                        <div className={style.section}>
                            <div className={style.sectionHeader}>Error Recovery</div>
                            <Stack tokens={{ childrenGap: 16, padding: "0 12px" }}>
                                <MessageBar messageBarType={MessageBarType.error}>
                                    {errorMessage}
                                </MessageBar>
                                <PrimaryButton
                                    text="Retry Workflow"
                                    onClick={handleRetryPlan}
                                    disabled={hasClickedRetry}
                                    iconProps={{ iconName: "Refresh" }}
                                />
                            </Stack>
                        </div>
                    )}
                </Stack>
            </div>
        </div>
    );
};
